import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { CalendarDays, MapPin, ArrowRight, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";

const DEFAULT_LINE = { label: "", price: 0, enabled: false };

// Read a pricing line safely from event.pricing (handles legacy flat numbers too).
const readLine = (pricing, key, fallbackLabel) => {
  const raw = pricing?.[key];
  if (raw && typeof raw === "object") {
    return {
      label: (raw.label || fallbackLabel || key).trim() || fallbackLabel,
      price: Number(raw.price || 0),
      enabled: raw.enabled !== false,
    };
  }
  if (typeof raw === "number") {
    return { label: fallbackLabel, price: raw, enabled: true };
  }
  return { ...DEFAULT_LINE, label: fallbackLabel };
};

const NumberStepper = ({ value, onChange, min = 0, max = 20, testId }) => (
  <div className="inline-flex items-center bg-brand-ivory rounded-full overflow-hidden border border-brand-maroon/10">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="p-2 hover:bg-brand-maroon/5 text-brand-maroon" data-testid={`${testId}-minus`}>
      <Minus className="w-4 h-4" />
    </button>
    <span className="w-10 text-center font-medium" data-testid={`${testId}-value`}>{value}</span>
    <button type="button" onClick={() => onChange(Math.min(max, value + 1))} className="p-2 hover:bg-brand-maroon/5 text-brand-maroon" data-testid={`${testId}-plus`}>
      <Plus className="w-4 h-4" />
    </button>
  </div>
);

export default function EventBooking() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    pet_parent_name: "",
    email: "",
    whatsapp_number: "",
    base_pack: "base_a",
    addon_extra_pets: 0,
    addon_humans_no_food: 0,
    addon_humans_with_food: 0,
    food_type: "veg",
    pet_name: "",
    pet_birthday_day: "",
    pet_birthday_month: "",
    policy_accepted: false,
  });

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => toast.error("Event not found")).finally(() => setLoading(false));
    api.get("/site-settings").then((r) => setSettings(r.data));
  }, [id]);

  // Extract pricing lines from event once loaded
  const lines = useMemo(() => {
    if (!event?.pricing) return null;
    return {
      base_a: readLine(event.pricing, "base_a", "1 human + 1 pet (with food)"),
      base_b: readLine(event.pricing, "base_b", "Solo human (with food)"),
      addon_pet: readLine(event.pricing, "addon_pet", "Additional pet"),
      addon_human_no_food: readLine(event.pricing, "addon_human_no_food", "Additional human (no food)"),
      addon_human_with_food: readLine(event.pricing, "addon_human_with_food", "Additional human (with food)"),
    };
  }, [event]);

  // If admin disabled the currently selected base pack, auto-switch to the other one.
  useEffect(() => {
    if (!lines) return;
    if (form.base_pack === "base_a" && !lines.base_a.enabled && lines.base_b.enabled) {
      setForm((f) => ({ ...f, base_pack: "base_b" }));
    } else if (form.base_pack === "base_b" && !lines.base_b.enabled && lines.base_a.enabled) {
      setForm((f) => ({ ...f, base_pack: "base_a" }));
    }
  }, [lines, form.base_pack]);

  // A pet is included when base_a is chosen (base_b is solo human).
  const petIncluded = form.base_pack === "base_a";
  const totalPets = (petIncluded ? 1 : 0) + form.addon_extra_pets;
  // Human food is included in either base pack (both currently ship with food).
  const foodIncluded = true;

  const total = useMemo(() => {
    if (!lines) return { total: 0, lines: [] };
    const breakdown = [];
    let t = 0;
    const base = lines[form.base_pack];
    if (base && base.enabled) {
      t += base.price;
      breakdown.push([base.label, 1, base.price]);
    }
    if (form.addon_extra_pets > 0 && lines.addon_pet.enabled) {
      const a = lines.addon_pet.price * form.addon_extra_pets;
      t += a;
      breakdown.push([lines.addon_pet.label, form.addon_extra_pets, a]);
    }
    if (form.addon_humans_no_food > 0 && lines.addon_human_no_food.enabled) {
      const a = lines.addon_human_no_food.price * form.addon_humans_no_food;
      t += a;
      breakdown.push([lines.addon_human_no_food.label, form.addon_humans_no_food, a]);
    }
    if (form.addon_humans_with_food > 0 && lines.addon_human_with_food.enabled) {
      const a = lines.addon_human_with_food.price * form.addon_humans_with_food;
      t += a;
      breakdown.push([lines.addon_human_with_food.label, form.addon_humans_with_food, a]);
    }
    // Early bird
    let eb = 0;
    if (event.early_bird_amount && event.early_bird_until) {
      try {
        const until = new Date(event.early_bird_until);
        if (new Date() <= until) {
          eb = event.early_bird_amount;
          t = Math.max(0, t - eb);
          breakdown.push([`Early bird discount`, 1, -eb]);
        }
      } catch (err) {
        console.warn("Early-bird date parse failed:", err);
      }
    }
    return { total: t, lines: breakdown, eb };
  }, [event, lines, form]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startRazorpayCheckout = async (bookingId) => {
    const sdkLoaded = await loadRazorpay();
    if (!sdkLoaded) throw new Error("Could not load Razorpay. Check your internet connection.");
    const orderRes = await api.post(`/bookings/${bookingId}/razorpay-order`);
    const { order_id, amount, currency, key_id, booking } = orderRes.data;

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: key_id,
        order_id,
        amount,
        currency,
        name: "Furshala",
        description: booking.event_title || "Furshala event booking",
        image: "https://customer-assets.emergentagent.com/job_b900a691-e276-4f55-b283-229aa9881e7a/artifacts/kduegc3q_furshala_logo_-removebg-preview.png",
        prefill: {
          name: booking.pet_parent_name,
          email: booking.email,
          contact: booking.whatsapp_number,
        },
        notes: { booking_id: bookingId },
        theme: { color: "#631F22" },
        handler: async (response) => {
          try {
            await api.post(`/bookings/${bookingId}/razorpay-verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            resolve(response);
          } catch (e) {
            reject(e);
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment cancelled")),
        },
      });
      rzp.on("payment.failed", async (resp) => {
        try { await api.post(`/bookings/${bookingId}/razorpay-failed`, resp?.error || {}); } catch (e) { /* non-fatal */ }
        reject(new Error(resp?.error?.description || "Payment failed"));
      });
      rzp.open();
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.policy_accepted) { toast.error("Please accept the cancellation policy"); return; }
    if (totalPets > 0 && (!form.pet_birthday_day || !form.pet_birthday_month)) {
      toast.error("Please share your pet's birthday so we can send wishes");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        event_id: id,
        ...form,
        humans_count: 1 + form.addon_humans_no_food + form.addon_humans_with_food,
        dogs_count: totalPets,
        pet_birthday_day: form.pet_birthday_day ? Number(form.pet_birthday_day) : null,
        pet_birthday_month: form.pet_birthday_month ? Number(form.pet_birthday_month) : null,
      };
      const res = await api.post("/bookings", payload);
      const bookingId = res.data.id;

      const useRazorpay = !!(settings?.razorpay_enabled && settings?.razorpay_key_id);
      if (useRazorpay) {
        try {
          await startRazorpayCheckout(bookingId);
          toast.success("Payment received!");
          nav(`/booking/${bookingId}/success`);
        } catch (err) {
          toast.error(err?.message || "Payment failed. Your booking is held — try again or contact us.");
          setSubmitting(false);
          return;
        }
      } else {
        await api.post(`/bookings/${bookingId}/mock-pay`);
        nav(`/booking/${bookingId}/success`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-20 text-center text-brand-soft/60">Loading…</div>;
  if (!event) return <div className="p-20 text-center text-brand-soft/60">Event not found.</div>;

  const showFoodPref = foodIncluded || form.addon_humans_with_food > 0;

  return (
    <div data-testid="event-booking-page">
      {/* Event header */}
      <section className="bg-brand-ivory">
        <div className="max-w-6xl mx-auto px-6 sm:px-12 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow mb-3">Book your spot</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-brand-soft leading-[1.05]">{event.title}</h1>
            <div className="mt-5 flex flex-wrap gap-4 text-sm text-brand-soft/70">
              <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-brand-maroon" /> {event.date} {event.time && `· ${event.time}`}</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand-maroon" /> {event.location}</span>
            </div>
            <p className="mt-5 text-brand-soft/80 leading-relaxed">{event.long_description || event.description}</p>
            {event.seats_note && (
              <div className="mt-4 inline-block bg-brand-maroon/10 text-brand-maroon px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide">{event.seats_note}</div>
            )}
          </div>
          {event.cover_image && (
            <div className="aspect-[4/3] rounded-3xl overflow-hidden border border-brand-maroon/10 shadow-lg">
              <img src={fileUrl(event.cover_image)} alt={event.title} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </section>

      {/* Booking form */}
      <section className="max-w-6xl mx-auto px-6 sm:px-12 py-14 sm:py-20 grid lg:grid-cols-3 gap-10">
        <form onSubmit={submit} className="lg:col-span-2 space-y-7 bg-white rounded-3xl border border-brand-maroon/10 p-7 sm:p-10" data-testid="booking-form">
          <div>
            <h2 className="font-serif text-3xl text-brand-soft">Your details</h2>
            <p className="text-sm text-brand-soft/60 mt-1">We&apos;ll send confirmation to your email and WhatsApp once payment is verified.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Pet parent name *" value={form.pet_parent_name} onChange={(v) => upd("pet_parent_name", v)} required testId="input-parent-name" />
            <Input label="Email *" type="email" value={form.email} onChange={(v) => upd("email", v)} required testId="input-email" />
            <Input label="WhatsApp number *" value={form.whatsapp_number} onChange={(v) => upd("whatsapp_number", v)} required testId="input-whatsapp" />
            {petIncluded && (
              <Input label="Pet name *" value={form.pet_name} onChange={(v) => upd("pet_name", v)} required testId="input-pet-name" />
            )}
          </div>

          {/* Pricing pack */}
          <div className="space-y-3">
            <div className="font-serif text-xl text-brand-soft">Choose your pack</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {lines?.base_a.enabled && (
                <PackOption
                  selected={form.base_pack === "base_a"}
                  onClick={() => upd("base_pack", "base_a")}
                  title={lines.base_a.label}
                  price={`₹${lines.base_a.price}`}
                  testId="pack-base-a"
                />
              )}
              {lines?.base_b.enabled && (
                <PackOption
                  selected={form.base_pack === "base_b"}
                  onClick={() => upd("base_pack", "base_b")}
                  title={lines.base_b.label}
                  price={`₹${lines.base_b.price}`}
                  testId="pack-base-b"
                />
              )}
            </div>
          </div>

          {/* Add-ons */}
          {(lines?.addon_pet.enabled || lines?.addon_human_no_food.enabled || lines?.addon_human_with_food.enabled) && (
            <div className="space-y-3">
              <div className="font-serif text-xl text-brand-soft">Add-ons</div>
              {lines.addon_pet.enabled && (
                <AddonRow label={lines.addon_pet.label} sub={`₹${lines.addon_pet.price} each · pet food included`} testId="addon-pet">
                  <NumberStepper value={form.addon_extra_pets} onChange={(v) => upd("addon_extra_pets", v)} testId="addon-pet-stepper" />
                </AddonRow>
              )}
              {lines.addon_human_no_food.enabled && (
                <AddonRow label={lines.addon_human_no_food.label} sub={`₹${lines.addon_human_no_food.price} each`} testId="addon-human-no-food">
                  <NumberStepper value={form.addon_humans_no_food} onChange={(v) => upd("addon_humans_no_food", v)} testId="addon-human-no-food-stepper" />
                </AddonRow>
              )}
              {lines.addon_human_with_food.enabled && (
                <AddonRow label={lines.addon_human_with_food.label} sub={`₹${lines.addon_human_with_food.price} each`} testId="addon-human-with-food">
                  <NumberStepper value={form.addon_humans_with_food} onChange={(v) => upd("addon_humans_with_food", v)} testId="addon-human-with-food-stepper" />
                </AddonRow>
              )}
            </div>
          )}

          {/* Food preference */}
          {showFoodPref && (
            <div className="space-y-2">
              <div className="font-serif text-xl text-brand-soft">Food preference</div>
              <div className="flex gap-3">
                {["veg", "non-veg"].map((opt) => (
                  <button key={opt} type="button" onClick={() => upd("food_type", opt)} className={`px-5 py-2 rounded-full text-sm border ${form.food_type === opt ? "bg-brand-maroon text-brand-cream border-brand-maroon" : "border-brand-maroon/30 text-brand-soft/80 hover:border-brand-maroon"}`} data-testid={`food-${opt}`}>
                    {opt === "veg" ? "Veg" : "Non-veg"}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Birthday - only when a pet is coming */}
          {totalPets > 0 && (
            <div className="space-y-2">
              <div className="font-serif text-xl text-brand-soft">Pet birthday * <span className="text-xs font-sans text-brand-soft/50 font-normal">(so we can send wishes)</span></div>
              <div className="grid grid-cols-2 gap-3 max-w-xs">
                <input type="number" min="1" max="31" placeholder="Day" value={form.pet_birthday_day} onChange={(e) => upd("pet_birthday_day", e.target.value)} required className="bg-brand-ivory rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid="input-birthday-day" />
                <select value={form.pet_birthday_month} onChange={(e) => upd("pet_birthday_month", e.target.value)} required className="bg-brand-ivory rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40" data-testid="input-birthday-month">
                  <option value="">Month</option>
                  {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                    <option key={i} value={i+1}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Policy checkbox */}
          <label className="flex items-start gap-3 bg-brand-ivory p-4 rounded-xl cursor-pointer" data-testid="policy-checkbox-label">
            <input type="checkbox" checked={form.policy_accepted} onChange={(e) => upd("policy_accepted", e.target.checked)} className="mt-1 w-4 h-4 accent-brand-maroon" data-testid="policy-checkbox" />
            <span className="text-sm text-brand-soft/80">
              I have read and agree to the <Link to="/refund-policy" target="_blank" className="underline text-brand-maroon">cancellation & refund policy</Link>.
            </span>
          </label>

          <button type="submit" disabled={submitting || event.is_sold_out} className="btn-primary-furshala inline-flex items-center gap-2 disabled:opacity-60" data-testid="submit-booking">
            {submitting ? "Processing…" : `Pay ₹${total.total} & Book`} <ArrowRight className="w-4 h-4" />
          </button>
          {event.is_sold_out && <div className="text-sm text-brand-maroon font-medium">Bookings are closed for this event.</div>}
        </form>

        {/* Summary sidebar */}
        <aside className="space-y-5">
          <div className="bg-white border border-brand-maroon/10 rounded-3xl p-7 sticky top-28" data-testid="booking-summary">
            <div className="font-serif text-2xl text-brand-soft mb-4">Order summary</div>
            <div className="space-y-3 text-sm">
              {total.lines.map((l, i) => (
                <div key={i} className="flex items-start justify-between gap-3">
                  <div className="text-brand-soft/80">{l[0]} {l[1] > 1 && <span className="text-brand-soft/50">× {l[1]}</span>}</div>
                  <div className={`font-medium ${l[2] < 0 ? "text-brand-olive" : "text-brand-soft"}`}>₹{l[2]}</div>
                </div>
              ))}
            </div>
            <div className="my-5 h-px bg-brand-maroon/10" />
            <div className="flex justify-between items-baseline">
              <div className="text-sm uppercase tracking-widest text-brand-soft/60">Total</div>
              <div className="font-serif text-3xl text-brand-maroon" data-testid="summary-total">₹{total.total}</div>
            </div>
            <div className="mt-5 text-xs text-brand-soft/60 leading-relaxed">
              Payment is securely processed via Razorpay. Your booking is confirmed once we verify the payment.
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}

const Input = ({ label, value, onChange, type = "text", required, testId }) => (
  <label className="block space-y-1.5">
    <div className="text-sm font-medium text-brand-soft">{label}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
      className="w-full bg-brand-ivory rounded-xl border-none px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-mustard/40"
      data-testid={testId}
    />
  </label>
);

const PackOption = ({ selected, onClick, title, price, testId }) => (
  <button type="button" onClick={onClick} className={`text-left p-5 rounded-2xl border transition-all ${selected ? "border-brand-maroon bg-brand-maroon/5" : "border-brand-maroon/15 hover:border-brand-maroon/40"}`} data-testid={testId}>
    <div className="flex items-baseline justify-between gap-3">
      <div className="font-serif text-lg text-brand-soft leading-tight">{title}</div>
      <div className="text-brand-maroon font-semibold whitespace-nowrap">{price}</div>
    </div>
  </button>
);

const AddonRow = ({ label, sub, children, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 bg-brand-ivory rounded-2xl p-4" data-testid={testId}>
    <div className="min-w-0 flex-1">
      <div className="font-medium text-brand-soft">{label}</div>
      <div className="text-xs text-brand-soft/60">{sub}</div>
    </div>
    <div className="flex-shrink-0 self-start sm:self-auto">{children}</div>
  </div>
);
